import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { map } from 'rxjs';
import { opacityAnimation } from '../../../animations/opacity.animation';
import { SidebarTypeEnum } from '../../../models/common/sidebar-type.enum';
import {
  CompanyStyleColorSizeGroupRow,
  CompanyStyleDetailsSchema,
  StyleColorShot,
} from '../../../models/company/company-style-details-schema.models';
import { Template } from '../../../models/company/template.models';
import { ShotStatusEnum } from '../../../shared/models/shot-status.enum';
import { TopbarService } from '../../../shared/services/topbar.service';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { GraphQLService } from '../../../services/graph-ql.service';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppService } from '../../../app.service';
import { FilterDialogData } from '../../../shared/models/types/filter-dialog-data.type';
import { Color } from '../../../models/common/color.model';
import { ColorFilterService } from '../../../shared/services/color-filter.service';
import { SizeGroup } from '../../../models/common/size-group.model';
import { SizeGroupFilterService } from '../../../shared/services/size-group-filter.service';
import { FilterService } from '../../../shared/services/filter.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from '../../../shared/components/filter-list/filter-list.component';
import { ActivatedRoute } from '@angular/router';
import { WrapperResultGraphQL } from '../../../models/shared/types/wrapper-result-graphql.type';

@Component({
  standalone: false,
  selector: 'bt-item-info',
  templateUrl: './item-info.component.html',
  styleUrls: ['./item-info.component.scss'],
  animations: [opacityAnimation]
})
export class ItemInfoComponent implements OnInit, OnChanges {

  readonly #graphQLService = inject(GraphQLService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #appService = inject(AppService);
  readonly #dialog = inject(MatDialog);
  readonly #filterService = inject(FilterService);
  readonly #topbarService = inject(TopbarService);
  readonly #activatedRoute = inject(ActivatedRoute);

  @Input() modelId!: number;
  @Output() exit = new EventEmitter<void>();

  styleDetails: { schema?: CompanyStyleDetailsSchema, tmpRow?: CompanyStyleColorSizeGroupRow, paginationPage?: number, paginationSize: number, search: string, activeShot?: StyleColorShot } = { paginationSize: 8, search: '' };
  loaderId = UtilityFunctions.newGuid();
  shotStatusEnum = ShotStatusEnum;
  actionComments!: string;
  sidebarLoaderId = UtilityFunctions.newGuid();
  imgDetailsElId = UtilityFunctions.newGuid();
  mainContainerElId = UtilityFunctions.newGuid();
  showGoToTopButton!: boolean;
  currentSidebarType!: SidebarTypeEnum;
  sidebarTypeEnum = SidebarTypeEnum;
  template!: Template;

  filtersList: Array<{ id: 'colors' | 'sizeGroups', label: string }> = [
    { id: 'colors', label: 'Colors' },
    { id: 'sizeGroups', label: 'Size Groups' }
  ];

  colors: FilterDialogData<Color> = {
    title: 'Colors',
    selected: [],
    filterService: inject(ColorFilterService),
    variables: { modelId: this.modelId }
  };
  sizeGroups: FilterDialogData<SizeGroup> = {
    title: 'Size Groups',
    selected: [],
    filterService: inject(SizeGroupFilterService)
  };

  constructor() {
    this.searchTextSubscription();
    this.queryParamsSubscribe();
  }

  @HostListener('window:scroll')
  public onWindowScroll() {
    setTimeout(() => {
      const devEl = document.getElementById(this.imgDetailsElId);
      const mainEl = document.getElementById(this.mainContainerElId);
      if (devEl && mainEl) {
        const offsetTop = devEl.offsetTop - 50;
        const docScrollTop = document.documentElement.scrollTop;
        const diff = docScrollTop - offsetTop;
        if (docScrollTop + 300 < mainEl.clientHeight) {
          this.showGoToTopButton = diff > 100;
          devEl.style.paddingTop = diff > 0 ? diff + 'px' : '0px';
        }
      } else if (devEl) {
        devEl.style.paddingTop = '0px';
      }
    });
  }

  public ngOnInit() {
    this.getCompanyStyleDetails(0);
  }

  ngOnChanges() {
    this.colors.variables.modelId = this.modelId;
  }

  openDialogFilter(title: string): void {
    const initialValue = [...this[title].selected];
    const dialog = this.#dialog.open(FilterListComponent<any>, {
      data: { title: this[title].title, selected: this[title].selected, filterService: this[title].filterService, variables: this[title].variables },
      height: '70%',
      width: 'auto',
      minWidth: '25%'
    });

    dialog.afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.applyFilters(title, res)
          } else {
            this[title].selected = initialValue;
          }
        }
      })
  }

  getLabel(filter: 'colors' | 'sizeGroups'): string {
    return this[filter].selected.length ? `: ${this[filter].selected.map(el => el.displayName).join(', ').slice(0, 20)}` : '';
  }

  getFullTitleFilter(filter: 'colors' | 'sizeGroups'): string {
    return this[filter].selected.length ? `${this[filter].selected.map(el => el.displayName).join(', ')}` : '';
  }

  clearFilter(event: any, filter: 'colors' | 'sizeGroups'): void {
    event.stopPropagation();
    this[filter].selected = [];
    (this[filter] as FilterDialogData<any>).filterService.clearSelection();
    this.#filterService.removeDataFromUrl(filter, () => null);
  }

  applyFilters(filter: string, selected: Array<any>) {
    if (!selected.length) {
      // Rimozione il parametro dall'url
      this.#filterService.removeDataFromUrl(filter, () => null);
    } else {
      this[filter].selected = selected;
      const ids = selected.map(el => `${el.displayId}`).join('-');
      this.#filterService.setQueryParams({ [filter]: ids });
    }
  }

  onBack() {
    this.exit.emit();
  }

  getCompanyStyleDetails(page: number) {
    if(!this.modelId) return;
    
    this.styleDetails.paginationPage = page;
    const colorIds = this.#activatedRoute.snapshot.queryParams['colors'] ? this.#activatedRoute.snapshot.queryParams['colors'].split('-').map((el: string) => +el) : [];
    const sizeGroupIds = this.#activatedRoute.snapshot.queryParams['sizeGroups'] ? this.#activatedRoute.snapshot.queryParams['sizeGroups'].split('-').map((el: string) => +el) : [];

    const filters = {
      currentUserId: this.#appService.user.userId,
      modelId: this.modelId,
      paginationPage: page,
      paginationSize: this.styleDetails.paginationSize,
      search: this.styleDetails.search,
      colorIds,
      sizeGroupIds
    }

    this.#graphQLService.getData<CompanyStyleDetailsSchema>(GetQuery.GET_COMPANY_STYLE_DETAILS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['companyStyleDetails']['nodes'][0]))
      .subscribe({
        next: (data: CompanyStyleDetailsSchema) => {
          const cloneData = structuredClone(data)
          if (!this.styleDetails.paginationPage) {
            this.styleDetails.schema = cloneData;
          } else {
            this.styleDetails.schema.detailsRows = this.styleDetails.schema.detailsRows.concat(cloneData.detailsRows);
            this.styleDetails.schema.colorsRowsLength += cloneData.colorsRowsLength;
          }
          if (!this.styleDetails.activeShot && this.styleDetails.schema.detailsRows && this.styleDetails.schema.detailsRows[0]?.shots) this.setActiveShot(this.styleDetails.schema.detailsRows[0], this.styleDetails.schema.detailsRows[0].shots[0]);
        }
      });
  }

  inViewportAction(ev: any, item: CompanyStyleColorSizeGroupRow) {
    item.isVisible = ev.isVisible;
    if (item.isVisible) {
      const index = this.styleDetails.schema.detailsRows.findIndex(el => el.colorId === item.colorId && (!item.sizeGroupId || item.sizeGroupId === el.sizeGroupId));
      if (index === this.styleDetails.schema.detailsRows.length - 1 && this.styleDetails.schema.colorsRowsLength / this.styleDetails.paginationSize === this.styleDetails.paginationPage + 1) {
        this.getCompanyStyleDetails(this.styleDetails.paginationPage + 1);
      }
    }
  }

  setActiveShot(row: CompanyStyleColorSizeGroupRow, shot: StyleColorShot, ev?: any) {
    if (ev) ev.stopPropagation();
    this.styleDetails.activeShot = shot;
    if (shot) this.styleDetails.activeShot.colorFullName = row.fullName;
  }

  changeImg(incr: number) {
    const row = this.styleDetails.schema.detailsRows.find(r => r.colorId === this.styleDetails.activeShot.colorId && (!this.styleDetails.activeShot.sizeGroupId || r.sizeGroupId === this.styleDetails.activeShot.sizeGroupId));
    if (row) {
      let index = row.shots.findIndex(x => x.shotTypeId === this.styleDetails.activeShot.shotTypeId) + incr;
      if (index < 0) {
        index = row.shots.length - 1;
      } else if (index >= row.shots.length) {
        index = 0;
      }
      this.setActiveShot(row, row.shots[index]);
    }
  }

  onReshoot() {
    this.currentSidebarType = SidebarTypeEnum.customSidebar;
  }

  reshoot() {
    const text = 'Rejected shot';
    this.actionComments = this.actionComments?.trim() ? text + ': ' + this.actionComments : text;
    // this.companyService.updateShotStatus(this.styleDetails.activeShot.shotId, ShotStatusEnum.Rejected, this.actionComments, this.sidebarLoaderId).subscribe(() => {
    //   this.onExitSidebar();
    //   this.styleDetails.activeShot.stateId = ShotStatusEnum.Rejected;
    // });
  }

  onExitSidebar() {
    this.currentSidebarType = null;
    this.actionComments = null;
    this.template = null;
    this.styleDetails.tmpRow = null;
  }

  onTemplateInfo(item: CompanyStyleColorSizeGroupRow) {
    item.showMenu = false;
    this.styleDetails.tmpRow = item;
    this.currentSidebarType = SidebarTypeEnum.customSidebar;
    // this.companyService.getStyleTemplate(this.styleDetails.schema.style.brandId, this.styleDetails.schema.style.modelId, item.colorId, item.sizeGroupId, this.sidebarLoaderId)
    //   .subscribe(data => this.template = data);
  }

  onReshootRow(item: CompanyStyleColorSizeGroupRow) {
    item.showMenu = false;
    this.styleDetails.tmpRow = item;
    this.currentSidebarType = SidebarTypeEnum.deleteAction;
  }

  reshootRow() {
    const p = this.styleDetails.tmpRow;
    const schemaId = this.styleDetails.schema.style.modelId;
    // this.companyService.reshootProduct(schemaId, p.colorId, p.sizeGroupId, this.sidebarLoaderId).subscribe(() => {
    //   this.onExitSidebar();
    //   this.getCompanyStyleDetails(0);
    // });
  }

  getData() {
    const params = this.#filterService.getDataFromUrl();
    this.getParams(params);
  }

  private getParams(params: { [key: string]: string }): void {
    if (params['colors']) {
      const ids = params['colors'].split('-').map(el => +el);
      this.getColors(ids);
    } else {
      this.colors.selected = [];
    }

    if (params['sizeGroups']) {
      const ids = params['sizeGroups'].split('-').map(el => +el);
      this.getSizeGroups(ids);
    } else {
      this.sizeGroups.selected = [];
    }

    if (params['search'])
      this.styleDetails.search = params['search'];
  }

  private getColors(colorIds: Array<number>) {
    const filters = { colorIds };
    this.#graphQLService.getData(GetQuery.GET_COLORS_BY_IDS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['colors']))
      .subscribe((data: WrapperResultGraphQL<any>) => {
        this['colors'].selected = data.nodes.map(el => ({ colorId: el.id, colorCode: el.ode, colorName: el.name, fullName: el.finalColorName, displayId: el.id!, displayName: el.finalColorName, disabled: false, selected: true }));
      });
  }

  private getSizeGroups(sizeGroupIds: Array<number>) {
    const filters = { sizeGroupIds };
    this.#graphQLService.getData(GetQuery.GET_SIZE_GROUPS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['sizeGroups']))
      .subscribe((data: WrapperResultGraphQL<any>) => {
        this['sizeGroups'].selected = data.nodes.map(el => ({ ...el, displayId: el.id!, displayName: el.name, disabled: false, selected: true }));
      });
  }


  private queryParamsSubscribe() {
    this.#activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        this.getData();
        this.getCompanyStyleDetails(0);
      });
  }

  private searchTextSubscription() {
    this.#topbarService.searchTextSubscribe
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(data => {
        if (data !== this.styleDetails.search) {
          this.styleDetails.search = data;
          this.getCompanyStyleDetails(0);
        }
      });
  }
}
